import * as React from 'react';
import { ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const ConversationContext = React.createContext<{
  scrollToBottom: (opts?: { smooth?: boolean }) => void;
  atBottom: boolean;
} | null>(null);

/**
 * Scrollable message list. Sized to fill its parent (parent must constrain
 * height). Tracks whether the user is at the bottom so the auto-scroll
 * helper knows when to skip pinning.
 */
export function Conversation({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [atBottom, setAtBottom] = React.useState(true);

  const scrollToBottom = React.useCallback((opts?: { smooth?: boolean }) => {
    const el = ref.current;
    if (!el) return;
    // Default to instant — smooth scrolling during streaming + layout
    // shifts (live block → persisted message swap) produces a visible
    // up-and-back jump as the smooth animation chases a moving target.
    el.scrollTo({
      top: el.scrollHeight,
      behavior: opts?.smooth ? 'smooth' : 'instant',
    });
  }, []);

  const onScroll = (): void => {
    const el = ref.current;
    if (!el) return;
    const distance = el.scrollHeight - (el.scrollTop + el.clientHeight);
    setAtBottom(distance < 60);
  };

  return (
    <ConversationContext.Provider value={{ scrollToBottom, atBottom }}>
      <div
        ref={ref}
        onScroll={onScroll}
        className={cn(
          'relative flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden px-3 py-3',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </ConversationContext.Provider>
  );
}

export function ConversationContent({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('mx-auto flex w-full max-w-3xl flex-col gap-4', className)} {...props}>
      {children}
    </div>
  );
}

export function ConversationScrollButton({ className }: { className?: string }) {
  const ctx = React.useContext(ConversationContext);
  if (!ctx || ctx.atBottom) return null;
  return (
    <Button
      onClick={() => ctx.scrollToBottom({ smooth: true })}
      variant="outline"
      size="icon"
      className={cn(
        'sticky bottom-4 left-1/2 z-10 -translate-x-1/2 self-center rounded-full shadow-md',
        className,
      )}
    >
      <ArrowDown className="size-4" />
    </Button>
  );
}

/**
 * Auto-scroll to bottom whenever `dep` changes — but only if the user was
 * already at the bottom. If they scrolled up to read, we don't yank them.
 */
export function useAutoScrollToBottom(dep: unknown) {
  const ctx = React.useContext(ConversationContext);
  React.useEffect(() => {
    if (ctx?.atBottom) ctx.scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dep]);
}
